import React, { useState, useEffect } from 'react'
import { string, bool, element, func } from 'prop-types'
import { connect } from 'react-redux'
import { Button } from '@livechat/design-system-react-components'

import { installApp, installAppNotLoggedIn } from '@/store/installedApps'
import {
  isAuthorized,
  isAvailableInCurrentPlan,
  hasInstallationProcessError,
  getPublishedApp,
  canInstallApplications,
} from '@/store/selectors'
import auth from '@/utils/auth'
import { useTypedDispatch } from '@/store/hooks'
import { logAmplitudeEvent } from '@/store/stats'
import useParamSelector from '@/hooks/useParamSelector'
import { AMPLITUDE_EVENTS } from '@/constants'

const eventName = `${AMPLITUDE_EVENTS.GET_CLICKED} - install app`

export const InstallButton = ({
  id,
  installApp,
  installAppForNotLoggedIn,
  isAuthorized,
  canInstallApplication,
  icon,
  size,
  disabled,
  className,
  fullWidth,
  redirect = true,
  hasInstallationError,
  kind,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useTypedDispatch()
  const publishedApp = useParamSelector(getPublishedApp, id)

  const eventPayload = publishedApp
    ? {
        appId: id,
        appName: publishedApp.name,
        price: publishedApp.payment ? 'paid' : 'free',
        authorized: isAuthorized,
      }
    : {}

  useEffect(() => {
    if (hasInstallationError) {
      setIsLoading(false)
    }
  }, [hasInstallationError])

  const installApplication = () => {
    installApp(id, redirect)
  }

  const authAndInstallApplication = () => {
    installAppForNotLoggedIn(id, redirect)
    auth.signIn()
  }

  const handleClick = (e) => {
    e.preventDefault()
    setIsLoading(true)

    if (isAuthorized) {
      if (canInstallApplication) {
        installApplication()
        dispatch(
          logAmplitudeEvent({
            name: eventName,
            params: eventPayload,
          })
        )
      }
    } else {
      authAndInstallApplication()
      dispatch(
        logAmplitudeEvent({
          name: eventName,
          params: eventPayload,
        })
      )
    }
  }

  return (
    <Button
      data-test="install-btn"
      icon={icon || ''}
      size={size}
      disabled={isLoading || disabled}
      loading={isLoading}
      onClick={handleClick}
      className={className}
      kind={kind || 'primary'}
      fullWidth={fullWidth}
    >
      Get
    </Button>
  )
}

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps

  return {
    hasInstallationError: hasInstallationProcessError(state),
    isAuthorized: isAuthorized(state),
    canInstallApplication: canInstallApplications(state),
    isAvailableInCurrentPlan: isAvailableInCurrentPlan(state, id),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    installApp: (id, redirect) => {
      dispatch(installApp.pending({ id, redirect }))
    },
    installAppForNotLoggedIn: (id, redirect) => {
      dispatch(
        installAppNotLoggedIn({
          id,
          redirect,
        })
      )
    },
  }
}

InstallButton.propTypes = {
  className: string,
  icon: element,
  size: string,
  disabled: bool,
  id: string,
  isAuthorized: bool,
  canInstallApplication: bool,
  isAvailableInCurrentPlan: bool,
  installApp: func,
  installAppForNotLoggedIn: func,
  fullWidth: bool,
  redirect: bool,
  hasInstallationError: bool,
  kind: string,
}

export default connect(mapStateToProps, mapDispatchToProps)(InstallButton)
